<template>
  <Teleport to="body">
    <div v-if="shouldShow" class="floating-unpaid-container">
      <!-- Floating Badge (Collapsed State) -->
      <Transition name="badge-pop">
        <div 
          v-if="!isExpanded"
          class="floating-badge"
          @click="expandPanel"
          @keydown.enter="expandPanel"
          @keydown.space.prevent="expandPanel"
          tabindex="0"
          role="button"
          aria-label="View unpaid items"
        >
          <div class="badge-icon-wrapper">
            <v-icon color="white" size="28">mdi-cart-outline</v-icon>
            <div 
              v-if="unpaidItemCount > 0" 
              class="badge-count"
            >
              {{ unpaidItemCount }}
            </div>
          </div>
          <div class="badge-amount">{{ userDataStore.getFormattedBalanceDue }}</div>
        </div>
      </Transition>

      <!-- Expanded Panel -->
      <Transition name="slide-fade">
        <div 
          v-if="isExpanded" 
          class="floating-panel"
          role="dialog"
          aria-labelledby="unpaid-panel-title"
          @keydown.esc="collapsePanel"
        >
          <div class="panel-header">
            <div class="d-flex flex-column">
              <h3 id="unpaid-panel-title" class="panel-title">Unpaid Items</h3>
              <p class="not-secured-text mb-4 pca-red">Registration is not secured until checkout/payment has been successfully processed.</p>
            </div>
            <v-btn
              icon="mdi-close"
              size="small"
              variant="text"
              color="pca-dark-grey"
              @click="collapsePanel"
              aria-label="Close unpaid items panel"
            />
          </div>
          <div class="panel-content">
            <UnPaidEntrants :showPayBalanceBtn="true" :isFloating="true" />
          </div>
        </div>
      </Transition>

      <!-- Backdrop -->
      <Transition name="fade">
        <div 
          v-if="isExpanded"
          class="floating-backdrop"
          @click="collapsePanel"
          aria-hidden="true"
        />
      </Transition>
    </div>
  </Teleport>
</template>

<script lang="ts">
// Components
import UnPaidEntrants from '@/components/home_page/UnPaidEntrants.vue';

// Stores
import { useAppDataStore } from '@/stores/appDataStore';
import { useUserDataStore } from '@/stores/userDataStore';

// Types
import { RouteName } from '@/router/routes';
import { 
  FormType, 
  PaymentSummaryCarItem, 
  PaymentSummaryEntrantItem, 
  PaymentSummaryEventItem, 
  PaymentSummaryMealItem 
} from '@/types/user';

// Vue
import { computed, defineComponent, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'FloatingUnpaidBalance',
  components: { UnPaidEntrants },
  
  setup() {
    // State
    const isExpanded = ref<boolean>(false);
    const appDataStore = reactive(useAppDataStore());
    const userDataStore = reactive(useUserDataStore());
    const route = useRoute();

    // Computed
    const shouldShow = computed(() => {
      return (
        !appDataStore.isLoading &&
        userDataStore.getAuthToken !== null && 
        (userDataStore.getHasUnpaidItems as unknown as boolean) && 
        route.name !== RouteName.CHECKOUT && 
        route.name !== RouteName.LOGIN &&
        route.name !== RouteName.HOME
      );
    });

    const unpaidItemCount = computed(() => {
      const summary = userDataStore.getPaymentSummary;
      let count = 0;
      
      // Count unpaid entrants (Primary, Co-Entrant, Child, Guest)
      count += (summary[FormType.PRIMARY_ENTRANT] as PaymentSummaryEntrantItem[]).filter(item => !item.paid).length;
      count += (summary[FormType.CO_ENTRANT] as PaymentSummaryEntrantItem[]).filter(item => !item.paid).length;
      count += (summary[FormType.CHILD] as PaymentSummaryEntrantItem[]).filter(item => !item.paid).length;
      count += (summary[FormType.GUEST] as PaymentSummaryEntrantItem[]).filter(item => !item.paid).length;
      
      // Count unpaid events
      count += (summary[FormType.EVENT] as PaymentSummaryEventItem[]).filter(item => !item.paid).length;
      
      // Count unpaid meals
      count += (summary[FormType.MEAL] as PaymentSummaryMealItem[]).filter(item => !item.paid).length;
      
      // Count unpaid cars
      count += (summary[FormType.CAR] as PaymentSummaryCarItem[]).filter(item => !item.paid).length;
      
      return count;
    });

    // Methods
    const expandPanel = (): void => {
      isExpanded.value = true;
      // Prevent body scroll when panel is open
      document.body.style.overflow = 'hidden';
    };

    const collapsePanel = (): void => {
      isExpanded.value = false;
      // Restore body scroll
      document.body.style.overflow = '';
    };

    // Watch route changes to auto-collapse
    watch(() => route.path, () => {
      if (isExpanded.value) {
        collapsePanel();
      }
    });

    // Watch shouldShow to ensure we collapse if conditions change
    watch(shouldShow, (newValue) => {
      if (!newValue && isExpanded.value) {
        collapsePanel();
      }
    });

    return {
      collapsePanel,
      expandPanel,
      isExpanded,
      shouldShow,
      unpaidItemCount,
      userDataStore,
    };
  },
});
</script>

<style scoped lang="scss">
.floating-unpaid-container {
  position: relative;
  z-index: 1000;
}

// Floating Badge Styles
.floating-badge {
  position: fixed;
  top: 80px;
  right: 38px;
  background: linear-gradient(135deg, $pca-red 0%, $pca-dark-red 100%);
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10001;
  outline: none;
  
  &:hover {
    transform: scale(1.08) translateY(-2px);
    box-shadow: 0 8px 28px rgba($pca-red, 0.5);
  }

  &:focus-visible {
    box-shadow: 0 4px 20px rgba($pca-red, 0.4), 
                0 0 0 3px rgba($pca-red, 0.3);
  }

  &:active {
    transform: scale(1.02);
  }
}

.badge-icon-wrapper {
  position: relative;
  margin-bottom: 2px;
}

.badge-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background: white;
  color: $pca-red;
  border-radius: 12px;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  font-weight: 700;
  padding: 0 5px;
}

.badge-amount {
  color: white;
  font-family: 'Saira Condensed', sans-serif;
  font-size: 15px;
  font-weight: 700;
  margin-top: 2px;
}

.badge-pulse {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: rgba($pca-red, 0.4);
  animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  pointer-events: none;
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.15);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

// Floating Panel Styles
.floating-panel {
  position: fixed;
  top: 110px;
  right: 20px;
  width: 440px;
  max-width: calc(100vw - 40px);
  max-height: calc(100vh - 140px);
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.18),
              0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 10002;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 2px solid $pca-light-grey;
  background: linear-gradient(to bottom, white 0%, rgba($pca-light-grey, 0.2) 100%);
}

.panel-title {
  color: $pca-red;
  font-family: 'Saira Condensed', sans-serif;
  font-size: 26px;
  font-weight: 500;
  margin: 0;
}

.panel-content {
  overflow-y: auto;
  flex: 1;
  padding: 16px;
  
  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba($pca-light-grey, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: $pca-light-blue;
    border-radius: 4px;
    
    &:hover {
      background: $pca-blue;
    }
  }
}

// Backdrop Styles
.floating-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  z-index: 10000;
}

// Animations
.badge-pop-enter-active {
  animation: badge-pop-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.badge-pop-leave-active {
  animation: badge-pop-out 0.3s ease-in;
}

@keyframes badge-pop-in {
  0% {
    transform: scale(0) rotate(-180deg);
    opacity: 0;
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

@keyframes badge-pop-out {
  0% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: scale(0) rotate(180deg);
    opacity: 0;
  }
}

.slide-fade-enter-active {
  animation: slide-fade-in 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-leave-active {
  animation: slide-fade-out 0.3s cubic-bezier(0.4, 0, 1, 1);
}

@keyframes slide-fade-in {
  0% {
    transform: translateX(100%) scale(0.9);
    opacity: 0;
  }
  100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
}

@keyframes slide-fade-out {
  0% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateX(100%) scale(0.9);
    opacity: 0;
  }
}

.fade-enter-active {
  animation: fade-in 0.3s ease;
}

.fade-leave-active {
  animation: fade-out 0.25s ease;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

// Responsive Design
@media (max-width: 768px) {
  .floating-badge {
    top: 15px;
    right: 15px;
    width: 70px;
    height: 70px;
  }

  .badge-amount {
    font-size: 13px;
  }

  .floating-panel {
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .panel-header {
    padding: 16px 20px;
  }

  .panel-title {
    font-size: 24px;
  }

  .panel-content {
    padding: 12px;
  }

  // Animations for mobile
  @keyframes slide-fade-in {
    0% {
      transform: translateY(100%) scale(0.95);
      opacity: 0;
    }
    100% {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }

  @keyframes slide-fade-out {
    0% {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
    100% {
      transform: translateY(100%) scale(0.95);
      opacity: 0;
    }
  }
}

@media (max-width: 480px) {
  .floating-badge {
    height: 60px;
    right: 5px;
    top: 60px;
    width: 60px;
  }

  .badge-amount {
    font-size: 12px;
  }
}

// High contrast mode support
@media (prefers-contrast: high) {
  .floating-badge {
    border: 3px solid white;
  }
}

// Reduced motion support
@media (prefers-reduced-motion: reduce) {
  .floating-badge,
  .floating-panel,
  .floating-backdrop {
    animation: none !important;
    transition: none !important;
  }

  .badge-pulse {
    display: none;
  }
}
</style>

